import { useState, useEffect, useCallback } from 'react';
import favoritesService from '../services/favoritesService';

/**
 * Custom hook for managing user's favorite bus stops
 * @param {boolean} isAuthenticated - Whether the user is authenticated
 * @param {string} [pathname] - Current route pathname (optional, for avoiding fetches on auth pages)
 * @returns {Object} { favorites, loading, error, addFavorite, removeFavorite, updateFavorite, isFavorite, getFavoriteLabel, refresh }
 */
const useFavorites = (isAuthenticated, pathname = '') => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }

    // Don't load favorites on auth pages - user is not actually logged in
    const authPages = ['/login', '/register', '/registration-success', '/forgot-password', '/reset-password', '/verify-email'];
    if (pathname && authPages.includes(pathname)) {
      setFavorites([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await favoritesService.getFavorites();
      setFavorites(data);
    } catch (err) {
      console.error('Failed to load favorites:', err);
      setError(err.message || 'Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, pathname]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const addFavorite = useCallback(async (busStopId, label = '', busStop = null) => {
    if (!isAuthenticated) return null;

    try {
      setError(null);
      const newFavorite = await favoritesService.addFavorite({ busStopId, label });
      // Refresh the entire list from server to get correct order
      await fetchFavorites();
      return newFavorite;
    } catch (err) {
      console.error('Failed to add favorite:', err);
      setError(err.message || 'Failed to add favorite');
      throw err;
    }
  }, [isAuthenticated, fetchFavorites]);

  const removeFavorite = useCallback(async (favoriteId) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      await favoritesService.deleteFavorite(favoriteId);
      setFavorites(prev => prev.filter(f => f.id !== favoriteId));
    } catch (err) {
      console.error('Failed to remove favorite:', err);
      setError(err.message || 'Failed to remove favorite');
      throw err;
    }
  }, [isAuthenticated]);

  const updateFavorite = useCallback(async (favoriteId, updateData) => {
    if (!isAuthenticated) return null;

    try {
      setError(null);
      const updated = await favoritesService.updateFavorite(favoriteId, updateData);
      setFavorites(prev => prev.map(f => f.id === favoriteId ? updated : f));
      return updated;
    } catch (err) {
      console.error('Failed to update favorite:', err);
      setError(err.message || 'Failed to update favorite');
      throw err;
    }
  }, [isAuthenticated]);

  const isFavorite = useCallback((busStopId) => {
    return favorites.some(f => f.busStopId === busStopId);
  }, [favorites]);

  const getFavorite = useCallback((busStopId) => {
    return favorites.find(f => f.busStopId === busStopId) || null;
  }, [favorites]);

  const getFavoriteLabel = useCallback((busStopId) => {
    const fav = favorites.find(f => f.busStopId === busStopId);
    return fav?.label || '';
  }, [favorites]);

  const reorderFavorites = useCallback(async (orderedIds) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      const reordered = await favoritesService.reorderFavorites(orderedIds);
      setFavorites(reordered);
      return reordered;
    } catch (err) {
      console.error('Failed to reorder favorites:', err);
      setError(err.message || 'Failed to reorder favorites');
      throw err;
    }
  }, [isAuthenticated]);

  const setFavoritesOrder = useCallback((newFavorites) => {
    setFavorites(newFavorites);
  }, []);

  return {
    favorites,
    loading,
    error,
    addFavorite,
    removeFavorite,
    updateFavorite,
    isFavorite,
    getFavorite,
    getFavoriteLabel,
    reorderFavorites,
    setFavoritesOrder,
    refresh: fetchFavorites
  };
};

export default useFavorites;
